import { useEffect } from 'react'
import { onCLS, onFCP, onINP, onLCP, onTTFB, type Metric } from 'web-vitals'

/**
 * Core Web Vitals thresholds based on Google's recommendations:
 * - LCP (Largest Contentful Paint): < 2.5s is good, > 4s is poor
 * - INP (Interaction to Next Paint): < 200ms is good, > 500ms is poor
 * - CLS (Cumulative Layout Shift): < 0.1 is good, > 0.25 is poor
 * - FCP (First Contentful Paint): < 1.8s is good, > 3s is poor
 * - TTFB (Time to First Byte): < 800ms is good, > 1800ms is poor
 */
const METRIC_THRESHOLDS = {
  LCP: { good: 2500, poor: 4000 },
  INP: { good: 200, poor: 500 },
  CLS: { good: 0.1, poor: 0.25 },
  FCP: { good: 1800, poor: 3000 },
  TTFB: { good: 800, poor: 1800 },
} as const

type MetricName = 'LCP' | 'INP' | 'CLS' | 'FCP' | 'TTFB'

function getRating(value: number, thresholds: { good: number; poor: number }): 'good' | 'needs-improvement' | 'poor' {
  if (value <= thresholds.good) return 'good'
  if (value <= thresholds.poor) return 'needs-improvement'
  return 'poor'
}

function formatMetric(value: number, name: MetricName): string {
  if (name === 'CLS') {
    return value.toFixed(3)
  }
  if (name === 'INP') {
    return `${Math.round(value)}ms`
  }
  return `${(value / 1000).toFixed(2)}s`
}

function handleMetric(metric: Metric) {
  const metricName = metric.name as MetricName
  const thresholds = METRIC_THRESHOLDS[metricName]

  if (!thresholds) return

  const rating = getRating(metric.value, thresholds)

  // Log to console in development
  if (import.meta.env.DEV) {
    const ratingEmoji = rating === 'good' ? '✅' : rating === 'needs-improvement' ? '⚠️' : '❌'
    console.log(
      `%c${ratingEmoji} ${metricName}: ${formatMetric(metric.value, metricName)} (${rating})`,
      rating === 'good'
        ? 'color: #22c55e; font-weight: bold'
        : rating === 'needs-improvement'
        ? 'color: #f59e0b; font-weight: bold'
        : 'color: #ef4444; font-weight: bold'
    )
  }

  // Send to analytics endpoint if available
  const analyticsEndpoint = import.meta.env.VITE_ANALYTICS_ENDPOINT
  if (analyticsEndpoint && typeof navigator !== 'undefined' && navigator.sendBeacon) {
    const body = JSON.stringify({
      name: metricName,
      value: metric.value,
      rating,
      delta: metric.delta,
      id: metric.id,
      timestamp: Date.now(),
    })

    // Use sendBeacon for non-blocking analytics
    navigator.sendBeacon(analyticsEndpoint, body)
  }
}

/**
 * WebVitals component that monitors and reports Core Web Vitals metrics.
 * Renders nothing - purely a monitoring side-effect component.
 *
 * Targets:
 * - LCP < 2.5s
 * - INP < 200ms (replaces FID in modern web-vitals)
 * - CLS < 0.1
 */
export function WebVitals() {
  useEffect(() => {
    // Only run in browser environment
    if (typeof window === 'undefined') return

    // Get CLS (Cumulative Layout Shift)
    onCLS(handleMetric)

    // Get INP (Interaction to Next Paint) - replaces FID
    onINP(handleMetric)

    // Get LCP (Largest Contentful Paint)
    onLCP(handleMetric)

    // Get FCP (First Contentful Paint)
    onFCP(handleMetric)

    // Get TTFB (Time to First Byte)
    onTTFB(handleMetric)
  }, [])

  // This component renders nothing
  return null
}
